export interface ProcessService {
  type: 'process';
  title: string;
  description: string;
  steps: string[];
}

export interface CourseEntry {
  name: string;
  desc: string;
  goals: string[];
}

export interface CoursesService {
  type: 'courses';
  title: string;
  tag: string;
  description: string;
  courses: CourseEntry[];
}

export type ServiceEntry = ProcessService | CoursesService;

export type ServiceKey = 'busqueda' | 'diagnostico' | 'lideres' | 'equipos' | 'rrhh';

export const servicesData: Record<ServiceKey, ServiceEntry> = {
  busqueda: {
    type: 'process',
    title: 'Reclutamiento',
    description:
      'Nuestro proceso de reclutamiento estratégico garantiza encontrar el talento ideal para tu organización.',
    steps: [
      'Relevamiento del perfil y cultura organizacional',
      'Publicación y búsqueda activa multicanal',
      'Entrevistas por competencias y evaluación',
      'Presentación de candidatos finalistas',
      'Seguimiento post-incorporación',
    ],
  },
  diagnostico: {
    type: 'process',
    title: 'Diagnóstico de Necesidades',
    description:
      'Análisis integral de tu organización para diseñar una hoja de ruta orientada a resultados medibles.',
    steps: [
      'Entrevistas con stakeholders clave',
      'Análisis de clima y cultura organizacional',
      'Mapeo de procesos y competencias',
      'Identificación de brechas y oportunidades',
      'Entrega de informe con plan de acción',
    ],
  },
  lideres: {
    type: 'courses',
    title: 'Formación de Líderes',
    tag: 'Crecimiento',
    description:
      'Programas diseñados para potenciar las habilidades de liderazgo en todos los niveles de tu organización.',
    courses: [
      {
        name: 'Liderazgo y conducción de equipo para supervisores',
        desc: 'Liderazgo situacional: Calibración del estilo de mando según el equipo y contexto.',
        goals: [
          'Calibración del estilo de mando según el equipo y contexto.',
          'Alineación estratégica: Organización de tareas enfocada en el cumplimiento de objetivos.',
          'Gestión del clima: Creación de entornos motivadores para mejorar el desempeño.',
          'Optimización de resultados: Foco en la eficiencia operativa y la productividad.',
        ],
      },
      {
        name: 'Comunicación efectiva para supervisores',
        desc: 'Transmisión de directivas precisas para evitar malentendidos.',
        goals: [
          'Claridad y autoridad: Transmisión de directivas precisas para evitar malentendidos.',
          'Reducción de errores: Herramientas para minimizar fallas operativas por falta de información.',
          'Resolución de conflictos: Gestión de desacuerdos mediante la comunicación asertiva.',
          'Canales prácticos: Implementación de herramientas de comunicación directa y eficiente.',
        ],
      },
      {
        name: 'Cómo marcar límites',
        desc: 'Definición de marcos de trabajo claros y compartidos.',
        goals: [
          'Establecimiento de reglas: Definición de marcos de trabajo claros y compartidos.',
          'Corrección de desvíos: Técnicas para rectificar comportamientos sin generar resistencia.',
          'Sostenimiento del vínculo: Preservación del compromiso del equipo al aplicar normas.',
          'Cumplimiento de estándares: Mantenimiento de la disciplina operativa y profesional.',
        ],
      },
      {
        name: 'El rol de jefe como coach',
        desc: 'Identificación y potenciación de habilidades individuales.',
        goals: [
          'Desarrollo de talento: Identificación y potenciación de habilidades individuales.',
          'Delegación efectiva: Transferencia de responsabilidades para optimizar la carga operativa.',
          'Potenciación del desempeño: Mejora de métricas individuales mediante el feedback constante.',
          'Transición de rol: Evolución del jefe operativo hacia un líder facilitador.',
        ],
      },
    ],
  },
  equipos: {
    type: 'courses',
    title: 'Cursos para Equipos',
    tag: 'Sinergia',
    description: 'Fortalecé la cohesión, comunicación y rendimiento de tus equipos de trabajo.',
    courses: [
      {
        name: 'Presentación personal y oratoria para colaboradores',
        desc: 'Mejora de la presencia y proyección dentro de la empresa.',
        goals: [
          'Imagen profesional: Mejora de la presencia y proyección dentro de la empresa.',
          'Habilidades de comunicación verbal y no verbal: Desarrollo de claridad oral frente a equipos y clientes.',
          'Impacto comunicativo: Técnicas para transmitir ideas con seguridad y convicción.',
          'Atención al cliente: Profesionalización del contacto externo y representación de marca.',
        ],
      },
      {
        name: 'Trabajo en equipo para colaboradores',
        desc: 'Fomento de la ayuda mutua para alcanzar metas grupales.',
        goals: [
          'Colaboración activa: Fomento de la ayuda mutua para alcanzar metas grupales.',
          'Responsabilidad compartida: Compromiso individual con el resultado colectivo.',
          'Eficiencia grupal: Eliminación de cuellos de botella en tareas conjuntas.',
          'Sinergia operativa: Mejora de la coordinación interna para agilizar procesos.',
        ],
      },
      {
        name: 'Comunicación efectiva para colaboradores',
        desc: 'Mejora en la transmisión de datos clave entre pares.',
        goals: [
          'Flujo de información: Mejora en la transmisión de datos clave entre pares.',
          'Eliminación de retrabajos: Reducción de tareas duplicadas por fallas de comunicación.',
          'Coordinación operativa: Sincronización de actividades mediante mensajes claros.',
          'Escucha activa: Comprensión precisa de requerimientos para optimizar la ejecución.',
        ],
      },
    ],
  },
  rrhh: {
    type: 'courses',
    title: 'Cursos para RRHH',
    tag: 'Gestión',
    description:
      'Formación técnica avanzada para profesionales de recursos humanos: gestión estratégica, selección por competencias, capacitación y experiencias significativas y habilidades blandas.',
    courses: [
      {
        name: 'Selección por competencias para RRHH',
        desc: 'Detección de habilidades específicas alineadas a la cultura.',
        goals: [
          'Identificación de perfiles: Detección de habilidades específicas alineadas a la cultura.',
          'Entrevistas profesionales: Metodologías para evaluar competencias de forma objetiva.',
          'Alineación cultural: Aseguramiento del fit entre el candidato y los valores de la empresa.',
          'Identificación de competencias: Reducción de la rotación mediante contrataciones precisas.',
        ],
      },
      {
        name: 'Aprendizajes significativos y capacitación interna para RRHH',
        desc: 'Creación de formaciones con aplicación directa al puesto.',
        goals: [
          'Diseño instruccional: Creación de formaciones con aplicación directa al puesto.',
          'Efectividad del aprendizaje: Métodos para garantizar que la teoría se traslade a la práctica.',
          'Mejora del desempeño: Generación de cambios medibles tras los procesos formativos.',
          'Objetivos de aprendizaje y operacionales: Estructuración de la capacitación interna como activo estratégico.',
        ],
      },
      {
        name: 'Indicadores de Clima Laboral para RRHH',
        desc: 'Definición de indicadores clave para medir la satisfacción interna.',
        goals: [
          'Métricas de clima: Definición de indicadores clave para medir la satisfacción interna.',
          'Análisis de datos: Interpretación de resultados para la prevención de conflictos.',
          'Estrategias de compromiso: Acciones basadas en evidencia para mejorar el engagement.',
          'Monitoreo organizacional: Seguimiento continuo del bienestar y la cultura interna.',
        ],
      },
      {
        name: 'Comunicación para colaboradores y RRHH',
        desc: 'Alineación de mensajes entre líderes y colaboradores.',
        goals: [
          'Coherencia institucional: Alineación de mensajes entre líderes y colaboradores.',
          'Fortalecimiento de cultura: Comunicación de valores para unificar el sentido de pertenencia.',
          'Desempeño organizacional: Relación entre mensajes claros y productividad colectiva.',
          'Transparencia interna: Mejora de la confianza a través de una comunicación formal sólida.',
        ],
      },
    ],
  },
};
